import React, { Component } from 'react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import Location from '../Location';
import './Timeseries.css'
import RadioLabelGroup from '../RadioLabelGroup'
import localize from '../localize';
import { getOffset, applyOffset } from '../../utils/time'

const getTimeseriesData = (timeseriesData, isp, location) => {
  if (!timeseriesData || !timeseriesData[location.id] || !timeseriesData[location.id][isp.name] || timeseriesData[location.id][isp.name].loading || timeseriesData[location.id][isp.name].error) {
    return false
  } else {
    return timeseriesData[location.id][isp.name]
  }
}

const notYetLoaded = (timeseriesData, isp, location) => {
  return !timeseriesData || !timeseriesData[location.id] || !timeseriesData[location.id][isp.name]
}

const enoughData = (data, key) => {
  var maxConsec = 0
  var consec = 0
  var last = -1
  data.forEach(d => {
    if ((last === -1 || parseInt(d.k, 10) === last + 3600) && (d[key] || d[key] === 0)) {
      consec += 1
    } else {
      maxConsec = Math.max(maxConsec, consec)
      consec = 1
    }
    last = parseInt(d.k, 10)
  })
  return Math.max(maxConsec, consec) >= 8
}

const getMin = (min, dividend) => {
  if (min <= 0) {
    return 0
  }

  var result = min
  while (result % dividend !== 0) {
    result--
  }
  return result
}

const getMax = (max, dividend) => {
  var result = max
  while (result % dividend !== 0) {
    result++
  }
  return result
}

const getTicks = ([min, max]) => {
  const step = (max - min) / 10
  return [min, min+step, min+step*2, min+step*3, min+step*4, min+step*5, min+step*6, min+step*7, min+step*8, min+step*9, max]
}

const config = {
  connection_score: { label: 'metrics.connection_score_full', domain: (max, min) => [getMin(Math.min(min - 5, 80), 10), 100] },
  latency: { label: 'metrics.latency', domain: (max, min) => [0, getMax(Math.max(max + 5, 60), 10)] },
  jitter: { label: 'metrics.jitter', domain: (max, min) => [0, getMax(Math.max(max + 2, 10), 10)] },
  packet_loss: { label: 'metrics.packet_loss', domain: (max, min) => [0, Math.ceil(Math.max(max, 2))] }
}

const CustomTooltip = localize()(({ i18n, payload, label, meta }) => {
  return (
    <table className="tooltip">
      <tbody>
        <tr className="tooltip-label">
          <th>{i18n.t(config[meta.metric].label)}</th>
          <th className="row-second">{label}</th>
        </tr>
        {
          payload.map(d => (
            <tr key={d.name} className={`tooltip-row tooltip-row-${d.name}`}>
              <td>{meta[d.name]}</td>
              <td className="row-second">{meta.format(d.value)}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  )
})

export class Timeseries extends Component {
  constructor(props) {
    super(props)

    this.setMetric = this.setMetric.bind(this)

    this.state = {
      metric: 'connection_score'
    }
  }

  setMetric(m) {
    this.setState({ metric: m })
  }

  componentWillMount() {
    const { dataA, dataB, location, timeseries, fetchTimeseries, shard } = this.props
    if (dataA && location && notYetLoaded(timeseries.timeseriesData, dataA, location)) {
      fetchTimeseries(location.id, dataA.name, shard)
    }
    if (dataB && location && notYetLoaded(timeseries.timeseriesData, dataB, location)) {
      fetchTimeseries(location.id, dataB.name, shard)
    }
  }

  componentDidUpdate() {
    const { dataA, dataB, location, timeseries, fetchTimeseries, shard } = this.props
    if (dataA && location && notYetLoaded(timeseries.timeseriesData, dataA, location)) {
      fetchTimeseries(location.id, dataA.name, shard)
    }
    if (dataB && location && notYetLoaded(timeseries.timeseriesData, dataB, location)) {
      fetchTimeseries(location.id, dataB.name, shard)
    }
  }

  render() {
    const { i18n, dataA, dataB, single, location, timeseries } = this.props
    const { metric } = this.state
    const dataObj = {}
    const tsA = dataA? getTimeseriesData(timeseries.timeseriesData, dataA, location) : false
    const tsB = dataB? getTimeseriesData(timeseries.timeseriesData, dataB, location) : false
    if (tsA) {
      const offset = getOffset(tsA.offset)
      tsA.data.forEach((d) => {
        const time = applyOffset(d.window_start, offset)
        const shiftTime = applyOffset(time, -28800)
        if (d.stats_ethernet) {
          if (!dataObj[shiftTime]) {
            dataObj[shiftTime] = {}
          }
          dataObj[shiftTime].utc = d.window_start
          dataObj[shiftTime].a = d.stats_ethernet[metric]
          dataObj[shiftTime].x = i18n.time(time)
        }
      })
    }
    if (tsB) {
      const offset = getOffset(tsB.offset)
      tsB.data.forEach((d) => {
        const time = applyOffset(d.window_start, offset)
        const shiftTime = applyOffset(time, -28800)
        if (d.stats_ethernet) {
          if (!dataObj[shiftTime]) {
            dataObj[shiftTime] = {}
          }
          dataObj[shiftTime].utc = d.window_start
          dataObj[shiftTime].b = d.stats_ethernet[metric]
          dataObj[shiftTime].x = i18n.time(time)
        }
      })
    }

    if (!tsA && !tsB) {
      return (
        <div/>
      )
    }

    const keys = Object.keys(dataObj).sort((a, b) => a - b)
    const data = keys.map(k => ({ ...dataObj[k], k: k }))
    data.sort((a, b) => a.k - b.k)
    data.push(data[0])

    const maxValue = data.reduce((a, v) => {
      var result = a
      if (v.a > result) {
        result = v.a
      }
      if (v.b > result) {
        result = v.b
      }
      return result
    }, 0)

    const minValue = data.reduce((a, v) => {
      var result = a
      if (v.a < result) {
        result = v.a
      }
      if (v.b < result) {
        result = v.b
      }
      return result
    }, Infinity)

    const tickFormats = {
      'connection_score': (x) => x,
      'latency': i18n.ms,
      'jitter': i18n.ms,
      'packet_loss': i18n.fractionalPercent
    }

    const metricOptions = [
      { label: i18n.t('metrics.connection_score_full'), value: 'connection_score' },
      { label: i18n.t('metrics.latency'), value: 'latency' },
      { label: i18n.t('metrics.packet_loss'), value: 'packet_loss' },
      { label: i18n.t('metrics.jitter'), value: 'jitter' }
    ]

    return (
      <div>
        <div className="timeseries-top-bar">
          <div className="compare-label">
            <div className="compare-providers">
              {i18n.t('pages.scores.leaderboard.compare')} <span className="compare-location"><Location location={location} hideCountry={true}/></span>
            </div>
          </div>
          <div className="compare-selector">
            <RadioLabelGroup options={metricOptions} value={metric} onChange={this.setMetric} />
          </div>
        </div>
        { single && enoughData(data, 'a') && <div className="timeseries-isp-label"><span><span className="line-key">&mdash;&mdash;</span>&nbsp;&nbsp;&nbsp;{dataA.name}</span></div> }
        <div className="timeseries-container">
          <div className="timeseries-better-worse">
            <div className={`timeseries-better text-${metric === 'connection_score'? 'better' : 'worse'}`}>{i18n.t(`pages.scores.compare.${metric === 'connection_score'? 'better' : 'worse'}`)} <q-icon size="sm" name="long-arrow-right"/></div>
            <div className={`timeseries-worse text-${metric === 'connection_score'? 'worse' : 'better'}`}><q-icon size="sm" name="long-arrow-left"/> {i18n.t(`pages.scores.compare.${metric === 'connection_score'? 'worse' : 'better'}`)}</div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 10, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid vertical={false} stroke="#373737" />
              { enoughData(data, 'a') && <Line dataKey="a" stroke="#da893a" strokeWidth={2} dot={false} activeDot={{ stroke: '#da893a' }} isAnimationActive={true} /> }
              { enoughData(data, 'b') && <Line dataKey="b" stroke="#277487" strokeWidth={2} dot={false} activeDot={{ stroke: '#277487' }} isAnimationActive={true} /> }
              <XAxis dataKey="x" axisLine={false} tick={{ fill: '#7e7777' }} tickLine={false} interval={1} padding={{left: 25, right: 25}} />
              <YAxis axisLine={false} tick={{ fill: '#7e7777' }} tickLine={false} interval={0} ticks={getTicks(config[metric].domain(maxValue, minValue))} domain={config[metric].domain(maxValue, minValue)} tickFormatter={tickFormats[metric]} />
              <Tooltip cursor={false} content={<CustomTooltip meta={{ a: dataA.name, b: dataB.name, metric: metric, format: tickFormats[metric] }} />} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="compare-disclaimer">
          <em>{i18n.t('pages.scores.compare.disclaimer')}</em>
        </div>
      </div>
    )
  }
}

export default localize()(Timeseries)



// WEBPACK FOOTER //
// ./src/components/Timeseries/index.js